/**
 * Phone provider configuration with display text and CSS class
 */
export interface PhoneProviderConfig {
    text: string;
    cssClass: string;
}

/**
 * Phone provider styling configurations
 */
export const PHONE_PROVIDER_CONFIGS: Record<string, PhoneProviderConfig> = {
    'Gmob': { text: 'Gmob', cssClass: 'bg-gray-200 text-gray-800' },
    'Itel': { text: 'Itel', cssClass: 'bg-gray-300 text-gray-800' },
    'Mobi': { text: 'Mobi', cssClass: 'bg-green-200 text-green-800' },
    'Reddi': { text: 'Reddi', cssClass: 'bg-red-600 text-white' },
    'Vietnam': { text: 'Vietnam', cssClass: 'bg-amber-800 text-white' },
    'Viettel': { text: 'Viettel', cssClass: 'bg-blue-200 text-blue-800' },
    'Vina': { text: 'Vina', cssClass: 'bg-yellow-200 text-yellow-800' },
    'Khác': { text: 'Khác', cssClass: 'bg-surface-200 text-surface-700' },
};

/**
 * Vietnamese phone provider lookup map
 * Maps international format prefixes (+84 XX) to provider names
 */
export const PHONE_PROVIDER_MAP: Record<string, string> = {
    '+8459': 'Gmob',
    '+8499': 'Gmob',
    '+8487': 'Itel',
    '+8470': 'Mobi',
    '+8476': 'Mobi',
    '+8477': 'Mobi',
    '+8478': 'Mobi',
    '+8479': 'Mobi',
    '+8489': 'Mobi',
    '+8490': 'Mobi',
    '+8493': 'Mobi',
    '+8455': 'Reddi',
    '+8452': 'Vietnam',
    '+8456': 'Vietnam',
    '+8458': 'Vietnam',
    '+8492': 'Vietnam',
    '+8432': 'Viettel',
    '+8433': 'Viettel',
    '+8434': 'Viettel',
    '+8435': 'Viettel',
    '+8436': 'Viettel',
    '+8437': 'Viettel',
    '+8438': 'Viettel',
    '+8439': 'Viettel',
    '+8486': 'Viettel',
    '+8496': 'Viettel',
    '+8497': 'Viettel',
    '+8498': 'Viettel',
    '+8481': 'Vina',
    '+8482': 'Vina',
    '+8483': 'Vina',
    '+8484': 'Vina',
    '+8485': 'Vina',
    '+8488': 'Vina',
    '+8491': 'Vina',
    '+8494': 'Vina',
};

/**
 * Get phone provider configuration from phone number
 * @param phoneNumber - Phone number in any format
 * @returns Provider configuration with text and cssClass
 */
export function getPhoneProviderConfig(phoneNumber: string): PhoneProviderConfig {
    if (!phoneNumber) return PHONE_PROVIDER_CONFIGS['Khác'];

    // Normalize the phone number to remove spaces, dashes, etc.
    const normalized = phoneNumber.replace(/[\s\-()]/g, '');

    let providerName: string | null = null;

    // Check if it starts with +84
    if (normalized.startsWith('+84')) {
        const prefix = normalized.substring(0, 5); // +84XX
        providerName = PHONE_PROVIDER_MAP[prefix] || null;
    }
    // Check if it starts with 84 (without +)
    else if (normalized.startsWith('84')) {
        const prefix = '+84' + normalized.substring(2, 4); // Convert to +84XX
        providerName = PHONE_PROVIDER_MAP[prefix] || null;
    }
    // Check if it starts with 0 (local format)
    else if (normalized.startsWith('0')) {
        const prefix = '+84' + normalized.substring(1, 3); // Convert 0XX to +84XX
        providerName = PHONE_PROVIDER_MAP[prefix] || null;
    }

    return PHONE_PROVIDER_CONFIGS[providerName || 'Khác'];
}

